import { inject, Injectable, Injector } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable, tap } from 'rxjs';
import { DeliveryType, SourceSystem } from 'hx-services';
import {
  ActorRef,
  ArchiveAttachmentsFilter,
  ArchiveClientTypeFilter,
  ArchiveDecorFilter,
  ArchiveOrderRow,
  ArchiveSearchKind,
  ArchiveStatus
} from '@callcenter-app/service/orders-archive.service';
import { ORDER_ACTED } from '@callcenter-app/service/order-acted.token';

/**
 * `orders-current.service.ts` — app-local HTTP client for `GET/PUT /api/vn/orders-current/**`, the
 * call-center-only resource behind the «Заказы» page (precedent: `orders-archive.service.ts`). Not part
 * of `hx-services`/`vn-api` on purpose: the resource is new and `tsgen` is not run for it.
 * Contract = `tmp/cc-orders-stage1-architect.md` §2. 1a: N1 `/list`, N2 `/rows`, N3 `/summary`,
 * N5 `/{id}/important`. 1b: N4 `/{id}` (live state), N6 `/{id}/cancel`, N7 `/{id}/edit-lock`,
 * N8 `/{id}/pay-invoice`. 1c (carts): N9 `POST /{id}/cart/prolong`, N10 `DELETE /{id}/cart`, `view=carts` / `cart_owner`.
 */

export const ORDERS_CURRENT_BASE = '/api/vn/orders-current';

// ---------- shared enums (HTTP values) ----------

export type OrderPhase = 'IN_WORK' | 'DONE' | 'CART';
export type DueGroup = 'LATE' | 'TODAY' | 'TOMORROW' | 'LATER';
/** `hc_orders.status_`. */
export type OrderStoredStatus = 'CART' | 'IN_PROGRESS' | 'COMPLETED';

/** `all` — every order of the page that is not in the archive: in work + not closed for over a week + finished (no carts; the owner, 24.10). */
export type OrdersView = 'all' | 'in_work' | 'late' | 'unpaid' | 'today' | 'done' | 'stale' | 'carts';
export type OrdersSort = 'due' | 'created';
/** Relative periods are resolved by the server on the store's own date; «Свои даты» go as `date_from`/`date_to`. */
export type OrdersPeriod = 'today' | 'tomorrow' | '7d';
export type OrdersPayment = 'unpaid' | 'paid' | 'debt';
export type CartOwner = 'mine' | 'operators' | 'online';
/** Segment of the in-work views (`status`). */
export type InWorkStatus = 'CREATED' | 'DECORED' | 'PACKED' | 'SHIPPED';
/** Segment of the «Завершены» view (`status`) — the archive's status classes. */
export type DoneStatus = ArchiveStatus;

// ---------- row model (§2.3) ----------

export interface CurrentDue {
  /** Seconds to the due moment (delivery: end of the slot; pickup: pickup time). Negative = late. */
  secondsLeft: number;
  /** Delivery only: seconds to the start of the slot (≤ 0 while the slot is running). */
  slotStartsInSeconds?: number;
  /** Receiving date minus the store's today. */
  dayOffset: number;
  group: DueGroup;
}

export interface CurrentLock {
  by: ActorRef;
  mine: boolean;
  heldSeconds: number;
}

/** Who a cart belongs to: an operator's reservation (КЦ / менеджер) or a cart the client fills in by himself (сайт, приложение, мессенджеры). */
export type CartGroup = 'OPERATOR' | 'ONLINE';

/** 1c — carts (§2.3). A reservation holds goods and time for a while (`app.cart.expire_seconds`, 10 minutes) and then lapses by itself. */
export interface CurrentCart {
  group: CartGroup;
  /** An online cart whose client has started to pay (`WAIT_PAYMENT`): `secondsLeft` then runs to the END OF THE PAYMENT TERM. */
  waitPayment: boolean;
  /** Seconds to the end of the reservation (or of the payment term) — present only while that moment is in the future. */
  secondsLeft?: number;
  /** Who holds the reservation (the actor of `ORDER_INITED`); an online cart has none. */
  owner?: ActorRef;
  /** The reservation is the current operator's own. */
  mine: boolean;
}

/** The live fields of a row (§2.3). Everything shown in wall-clock form is already in the store's own time. */
export interface CurrentRow {
  phase: OrderPhase;
  orderStatus: OrderStoredStatus;
  /** The real `action_` — the archive's class (`row.status`) is only meaningful for `phase === 'DONE'`. */
  action: string;
  important: boolean;
  /** `payment_date_` is empty and it is not a debt — the ONLY payment signal of the live branch. */
  unpaid: boolean;
  createdAt?: string;
  modified: boolean;
  modifiedAt?: string;
  /** `/list` only, in-work only — `/rows` and N4 never send it (the page keeps the previous value). */
  newClient?: boolean;
  stale?: boolean;
  due?: CurrentDue;
  lock?: CurrentLock;
  invoiceSentAt?: string;
  courier?: { id: number; name?: string };
  finishedAt?: string;
  cart?: CurrentCart;
  /**
   * 1d: the last not-removed invoice went out but Kaspi never confirmed it — «счёт отправлен 11:03» may not have reached the client.
   * Only `true` (absent otherwise); next to `invoiceSentAt`. The state carries the same in `invoice.unconfirmed`.
   */
  invoiceUnconfirmed?: boolean;
}

/** What the server sends: the archive row from the shared builder + the live fields. */
export interface CurrentOrderRowModel {
  row: ArchiveOrderRow;
  current: CurrentRow;
}

/**
 * The flat row the shared `app-archive-row` receives (§4.4): the archive row without its `status`/`action`
 * pair (which are the archive's final-class placeholders for an unfinished order) + the real `action`
 * and the live block.
 */
export type CurrentOrderRow = Omit<ArchiveOrderRow, 'status' | 'action'> & {
  status?: ArchiveStatus;
  action: string;
  current: CurrentRow;
};

export function flattenCurrentRow(item: CurrentOrderRowModel): CurrentOrderRow {
  return {
    ...item.row,
    status: item.current.phase === 'DONE' ? item.row.status : undefined,
    action: item.current.action,
    current: item.current,
  };
}

export function isCurrentOrderRow(row: ArchiveOrderRow | CurrentOrderRow): row is CurrentOrderRow {
  return (row as CurrentOrderRow).current !== undefined;
}

// ---------- GET /list ----------

/** Search context of the «поиск» mode (§2.2) — the archive's, minus the fields this API never sets. */
export interface CurrentSearchContext {
  kind: ArchiveSearchKind;
  normalized: string;
  addressSkipped?: boolean;
  clientsCapped?: boolean;
}

export interface CurrentOrderPageModel {
  list: CurrentOrderRowModel[];
  hasNext: boolean;
  next?: { cursor: string };
  /** «поиск» and «клиент» modes, first page only — M for «Показано N из M» (cap 500 → `totalCapped`). */
  total?: number;
  totalCapped?: boolean;
  search?: CurrentSearchContext;
}

export interface CurrentOrderPage {
  list: CurrentOrderRow[];
  hasNext: boolean;
  next?: { cursor: string };
  total?: number;
  totalCapped?: boolean;
  search?: CurrentSearchContext;
}

/** Filters shared by `/list` (view mode) and `/summary` — page keys are camelCase, HTTP params snake_case 1:1. */
export interface CurrentFilterParams {
  period?: OrdersPeriod;
  dateFrom?: string;
  dateTo?: string;
  countryIds?: number[];
  cityIds?: number[];
  storeIds?: number[];
  deliveryType?: DeliveryType;
  payment?: OrdersPayment;
  operatorId?: number;
  important?: boolean;
  sources?: SourceSystem[];
  decor?: ArchiveDecorFilter;
  attachments?: ArchiveAttachmentsFilter;
  clientType?: ArchiveClientTypeFilter;
  productInfoIds?: number[];
  courierId?: number;
  /** Only `done`: `false` shows the cashbox sales without a client (the server default is `true`). */
  withClient?: boolean;
}

export interface CurrentListParams extends CurrentFilterParams {
  view?: OrdersView;
  /** Segment: `InWorkStatus` for the in-work views, `DoneStatus` for `done`. */
  status?: InWorkStatus | DoneStatus;
  cartOwner?: CartOwner;
  sort?: OrdersSort;
  /** Search mode — wins over `view`; no filters accompany it. */
  q?: string;
  /** Client mode — wins over everything. */
  clientId?: number;
  limit?: number;
  /** Opaque cursor from `CurrentOrderPage.next.cursor` — never parsed on the frontend. */
  cursor?: string;
}

export interface CurrentSummaryParams extends CurrentFilterParams {
  view?: OrdersView;
  status?: InWorkStatus | DoneStatus;
  cartOwner?: CartOwner;
  /** Full loads ask for the «Завершены» count and the «не закрыты больше недели» counter. */
  withDone?: boolean;
  withStale?: boolean;
}

// ---------- GET /summary (§2.5) ----------

export interface CurrentSummaryTiles {
  inWork: number;
  late: number;
  unpaid: number;
  today: number;
  /** 1c: active carts counted with the filters that apply to carts (§2.2). */
  carts?: number;
  /** 1c (assumed, see the 1c report): `carts` split into operators' reservations and online carts — the tile's «6 операторов · 5 с сайта». */
  cartsOperators?: number;
  cartsOnline?: number;
  /** `with_done=true` or `view=done`. */
  done?: number;
  /** `with_stale=true` or `view=stale`; the server caps it at 1000 → `staleCapped`. */
  stale?: number;
  staleCapped?: boolean;
  /** «Все» = in work + stale + done with the same filters: `view=all`, or a full load (`with_done` + `with_stale`); `allCapped` — the stale part reached its ceiling. */
  all?: number;
  allCapped?: boolean;
}

export interface CurrentSummaryModel {
  tiles: CurrentSummaryTiles;
  /** Segment counters of the active view: [ALL, CREATED, DECORED, PACKED, SHIPPED] | [ALL, RECEIVED, CANCELLED, REFUNDED] | both for `all` | [ALL, MINE, OPERATORS, ONLINE]; empty for `stale`. */
  segments: Array<{ key: string; count: number }>;
  /** Active view + segment + filters = M in «Показано N из M». */
  found: number;
  /** The largest `id_` in the active view + segment + filters — for the «появились новые заказы» chip. */
  maxId?: number;
  /** View «в работе», `sort=due`. */
  groups?: { late: number; today: number; tomorrow: number; later: number };
  /** `view=done`. */
  days?: Array<{ day: string; count: number }>;
}

// ---------- PUT /{id}/important ----------

export interface CurrentImportantModel {
  important: boolean;
}

// ---------- GET /{orderId} — live state of one order (§2.6, 1b) ----------

/** `CART_CLOSED` (1c): a reservation that was released or has lapsed — only `can` (all false) comes with it. */
export type StatePhase = OrderPhase | 'CART_CLOSED';

/** What the operator may do with the order right now — ONE rule on the server (`CurrentActionRules`), re-checked by every action. */
export interface CurrentCan {
  edit: boolean;
  cancel: boolean;
  invoice: boolean;
  favourite: boolean;
  /** 1c — carts. */
  continueCart: boolean;
  prolongCart: boolean;
  releaseCart: boolean;
}

/** Why an action is off: a reason code (`SHIPPED`, `DONE`, `PAID`, `DEBT`, `LEGAL`, `NO_KASPI`…) → i18n `ordersCurrent.cannot.<action>.<code>`. */
export interface CurrentCannot {
  edit?: string;
  cancel?: string;
  invoice?: string;
  /** 1c (assumed keys, see the 1c report): `ONLINE_CART` / `NOT_OWN_CART` / `CART_EXPIRED` → i18n `ordersCurrent.cannot.cart.<code>`. */
  continueCart?: string;
  prolongCart?: string;
  releaseCart?: string;
}

/** The reason codes of the three cart actions (§2.6). */
export type CartCannotCode = 'ONLINE_CART' | 'NOT_OWN_CART' | 'CART_EXPIRED';

/** How a reservation ended: the operator released it (`RELEASED`) or its term ran out and a job released it (`EXPIRED`). */
export type CartClosedReason = 'RELEASED' | 'EXPIRED';

/** `CART_CLOSED` (1c, §2.6 + the PM's clarification): when and why the reservation ended. `at` is on the store's clock, like every other time. */
export interface CurrentCartClosed {
  at?: string;
  reason: CartClosedReason;
  by?: ActorRef;
  /**
   * 1d — what a live cart says about itself, so that a reservation opened «cold» (by the link) has the header it had alive; all absent when there is
   * nothing to say. `group` as `current.cart.group`; `startedAt` as `current.createdAt` (the store's clock); `owner` — whose reservation it was (with the
   * position, never the number: nothing is looked up for a closed cart), only for a reservation of an operator; `mine` — the caller's own.
   */
  group?: CartGroup;
  startedAt?: string;
  owner?: ActorRef;
  mine?: boolean;
}

/** The last (not removed) `PAY_INVOICE_SENT`. `sentAt` is on the store's clock; `phone` is digits only. */
export interface CurrentInvoice {
  sentAt: string;
  /** When the invoice was sent the LAST time (store's clock) — only after «Отправить ещё раз»; the text «отправлен в …» prefers it. */
  lastSentAt?: string;
  phone: string;
  amount: number;
  by?: ActorRef;
  count: number;
  /**
   * The invoice went out but Kaspi never confirmed it — it may not have reached the client. N8 to the SAME phone then sends it again
   * (no `ALREADY_SENT`; the answer is `SENT` or `UNCONFIRMED` once more, or `invoiceTooFrequent` / `invoiceLimit` / `invoiceSendFailed`).
   * Absent or `false` — the invoice is confirmed, and a repeat to the same phone and sum answers `ALREADY_SENT`.
   */
  unconfirmed?: boolean;
}

/** Only when `can.invoice`: what the invoice dialog needs. */
export interface CurrentKaspi {
  /**
   * The format of N8's `phone`: the country code (`"7"`) and the length of the national part (`10`) — «+7» and ten digits, as the
   * dialog's field is drawn. (Earlier the store country's own cut came here, «+7 7» and 9; the dialog never relied on it.)
   */
  phonePrefix: string;
  phoneLength: number;
  /** The order's own phone in the format `pay-invoice` takes (`7XXXXXXXXXX`); absent when the order has none or it is not such a number. */
  defaultPhone?: string;
  /** The client's bonuses are applied to the order — the invoice sum is the total minus the bonuses. */
  coinsApplied?: boolean;
}

/** Same shape as the card's `receiving` — the drawer lays these fields over the card's. */
export interface CurrentReceiving {
  type: string;
  plannedAt: string;
  slot?: { date: string; from: string; to: string };
}

export interface CurrentOrderState {
  id: number;
  phase: StatePhase;
  /** Built by the same code as the `current` of a list row — a row and the drawer cannot disagree. */
  current?: CurrentRow;
  can: CurrentCan;
  cannot?: CurrentCannot;
  invoice?: CurrentInvoice;
  kaspi?: CurrentKaspi;
  receiving?: CurrentReceiving;
  /** Only with `phase === 'CART_CLOSED'` (1c). */
  cartClosed?: CurrentCartClosed;
}

/** The state of an order the live look can draw: it carries the live block. */
export type LiveOrderState = CurrentOrderState & { current: CurrentRow };

/** The state has something to show as a live order (1c adds `CART`). */
export function isLiveState(state: CurrentOrderState | undefined): state is LiveOrderState {
  return !!state && !!state.current && (state.phase === 'IN_WORK' || state.phase === 'DONE' || state.phase === 'CART');
}

/** A reservation that was released or has lapsed (1c): nothing but the news about it comes, every action is off. */
export function isClosedCartState(state: CurrentOrderState | undefined): state is CurrentOrderState {
  return !!state && state.phase === 'CART_CLOSED';
}

export const NO_CART_ACTIONS: CurrentCan = {
  edit: false, cancel: false, invoice: false, favourite: false, continueCart: false, prolongCart: false, releaseCart: false,
};

/**
 * What the drawer draws for a closed reservation: the state itself carries no live block (or none worth reading), so it
 * takes the last block the drawer knew — the owner, the start, the group — with the countdown taken off, or a bare
 * cart block when the drawer opened on a reservation that was already closed. Every action is off.
 */
export function closedCartState(raw: CurrentOrderState, last?: CurrentRow): LiveOrderState {
  const base: CurrentRow = raw.current ?? last ?? { phase: 'CART', orderStatus: 'CART', action: 'CANCELLED', important: false, unpaid: false, modified: false };
  const closed = raw.cartClosed;
  const createdAt = base.createdAt ?? closed?.startedAt;
  return {
    ...raw,
    phase: 'CART_CLOSED',
    can: NO_CART_ACTIONS,
    current: { ...base, phase: 'CART', orderStatus: 'CART', ...(createdAt ? { createdAt } : {}), cart: closedCartBlock(base.cart, closed) },
  };
}

/**
 * The cart block of a closed reservation. 1d: a reservation opened «cold» (by the link — no live block before) has the header it had alive, because the
 * state itself says whose it was and since when (`cartClosed.owner / mine / group / startedAt`). What the drawer saw alive wins; the state fills in what
 * the block lacks. Nothing known — no block, nothing made up.
 */
function closedCartBlock(last: CurrentCart | undefined, closed: CurrentCartClosed | undefined): CurrentCart | undefined {
  if (last) {
    const owner = last.owner ?? closed?.owner;
    return { ...last, secondsLeft: undefined, ...(owner ? { owner } : {}), mine: last.mine || closed?.mine === true };
  }
  if (closed && (closed.group || closed.owner || closed.mine)) {
    return { group: closed.group ?? 'OPERATOR', waitPayment: false, ...(closed.owner ? { owner: closed.owner } : {}), mine: closed.mine === true };
  }
  return undefined;
}

// ---------- POST /{orderId}/cancel (§2.7 N6) ----------

export const CANCEL_REASON_CODES = ['CLIENT_CHANGED_MIND', 'NO_ANSWER', 'NO_PRODUCT', 'CANNOT_MAKE_IN_TIME', 'DUPLICATE', 'OTHER'] as const;
export type CancelReasonCode = typeof CANCEL_REASON_CODES[number];
/** The server's limit for the cancel comment. */
export const CANCEL_COMMENT_MAX = 500;

export interface CurrentCancelRequest {
  reasonCode: CancelReasonCode;
  /** ≤ 500 characters; obligatory for `OTHER`. */
  comment?: string;
}

// ---------- POST /{orderId}/pay-invoice (§2.7 N8) ----------

/**
 * `SENT` — Kaspi took the invoice; `ALREADY_SENT` — the same phone and sum already have a CONFIRMED one (nothing was sent again);
 * `UNCONFIRMED` — Kaspi did not answer in time: the invoice may or may not have reached the client (it is then sent again to the
 * same phone on request — see {@link CurrentInvoice.unconfirmed}).
 */
export type PayInvoiceResult = 'SENT' | 'ALREADY_SENT' | 'UNCONFIRMED';

/** What N8 takes as `phone`: the country code `7` and ten digits. */
const PAY_INVOICE_PHONE = /^7\d{10}$/;

/** The digits of a phone in N8's format (`7XXXXXXXXXX`); `undefined` for anything else — an invoice cannot be sent again to such a number as it is. */
export function payInvoicePhone(phone: string | undefined): string | undefined {
  const digits = (phone ?? '').replace(/\D/g, '');
  return PAY_INVOICE_PHONE.test(digits) ? digits : undefined;
}

export interface CurrentPayInvoice {
  result: PayInvoiceResult;
  total: number;
  sentAt: string;
  phone: string;
}

@Injectable({
  providedIn: 'root'
})
export class OrdersCurrentService {

  /**
   * Orders for which THIS browser session sent a Kaspi invoice (§4.5): only their `unpaid: true → false`
   * transition in a polled `/rows` answer raises the «Заказ … оплачен» notice (payments taken at the till
   * or by couriers on other people's orders stay silent). Filled by the invoice dialog (1b).
   */
  private invoiceOrderIds = new Set<number>();
  /** Asked when the token is needed, not at the construction: the hook (`CallLinkService`) depends on services that depend on this one. */
  private readonly injector = inject(Injector);

  constructor(
    private http: HttpClient,
  ) {
  }

  getList(params: CurrentListParams): Observable<CurrentOrderPage> {
    return this.http.get<CurrentOrderPageModel>(`${ORDERS_CURRENT_BASE}/list`, { params: this.toListParams(params) }).pipe(
      map(page => ({ ...page, list: page.list.map(flattenCurrentRow) }))
    );
  }

  /** Fresh rows for up to 100 ids; ids the operator can't see (or that left `hc_orders`) are silently omitted. */
  getRows(ids: number[]): Observable<CurrentOrderRow[]> {
    let params = new HttpParams();
    ids.forEach(id => {
      params = params.append('ids', String(id));
    });
    return this.http.get<CurrentOrderRowModel[]>(`${ORDERS_CURRENT_BASE}/rows`, { params }).pipe(
      map(list => list.map(flattenCurrentRow))
    );
  }

  getSummary(params: CurrentSummaryParams): Observable<CurrentSummaryModel> {
    return this.http.get<CurrentSummaryModel>(`${ORDERS_CURRENT_BASE}/summary`, { params: this.toSummaryParams(params) });
  }

  /** `PUT` sets the value — a repeat is harmless (§2.7). The caller owns the optimistic update and the rollback. */
  setImportant(orderId: number, value: boolean): Observable<CurrentImportantModel> {
    return this.http.put<CurrentImportantModel>(`${ORDERS_CURRENT_BASE}/${orderId}/important`, { value });
  }

  /** N4 — the live state of one order. An archived order (or one the page does not show) answers `ordersArchive.orderNotFound`. */
  getState(orderId: number): Observable<CurrentOrderState> {
    return this.http.get<CurrentOrderState>(`${ORDERS_CURRENT_BASE}/${orderId}`);
  }

  /** N6 — cancel with a reason. A repeat answers `duplicateOperation` (= already cancelled). */
  cancelOrder(orderId: number, request: CurrentCancelRequest): Observable<CurrentOrderState> {
    return this.http.post<CurrentOrderState>(`${ORDERS_CURRENT_BASE}/${orderId}/cancel`, request);
  }

  /** N7 — take the edit lock before the editor opens. Somebody else holds it → `ordersCurrent.lockedByOther{fullname, heldSeconds}`. */
  takeEditLock(orderId: number): Observable<CurrentOrderState> {
    return this.http.post<CurrentOrderState>(`${ORDERS_CURRENT_BASE}/${orderId}/edit-lock`, {});
  }

  /**
   * N9 (1c) — prolong the operator's own reservation: the server sets the term to «now + `app.cart.expire_seconds`» (10 minutes),
   * whatever was left. Answers the new state. A reservation that has already lapsed answers `cart.expired`.
   */
  prolongCart(orderId: number): Observable<CurrentOrderState> {
    return this.http.post<CurrentOrderState>(`${ORDERS_CURRENT_BASE}/${orderId}/cart/prolong`, {});
  }

  /**
   * N10 (1c) — release the operator's own reservation (204). A repeat answers `duplicateOperation` = already released = success.
   * Stage 4 (B2′): a release that was made tells the link of a call (`ORDER_ACTED` `released`) — the one place every «Снять бронь» passes (the page of the booking, the held booking of step 1, the card of the workplace, the shutter), so
   * that a call waiting for the order of the held one opens. A page that has no such hook (the specs, the old pages) does nothing more than it did.
   */
  releaseCart(orderId: number): Observable<void> {
    return this.http.delete<void>(`${ORDERS_CURRENT_BASE}/${orderId}/cart`).pipe(
      tap(() => this.injector.get(ORDER_ACTED, null)?.acted({ kind: 'released', orderId })),
    );
  }

  /**
   * N8 — one Kaspi invoice. `phone`: digits only, with the country prefix (`^7\d{10}$`); the sum is the server's. A repeat to the same
   * phone and sum answers `ALREADY_SENT` for a confirmed invoice and sends it again for an unconfirmed one (`invoice.unconfirmed`).
   */
  sendPayInvoice(orderId: number, phone: string): Observable<CurrentPayInvoice> {
    return this.http.post<CurrentPayInvoice>(`${ORDERS_CURRENT_BASE}/${orderId}/pay-invoice`, { phone });
  }

  markInvoiceSent(orderId: number): void {
    this.invoiceOrderIds.add(orderId);
  }

  hasInvoiceInSession(orderId: number): boolean {
    return this.invoiceOrderIds.has(orderId);
  }

  private toListParams(p: CurrentListParams): HttpParams {
    let params = this.toFilterParams(p);
    const set = setter(() => params, next => params = next);
    set('view', p.view);
    set('status', p.status);
    set('cart_owner', p.cartOwner);
    set('sort', p.sort);
    set('q', p.q);
    set('client_id', p.clientId);
    set('limit', p.limit);
    set('cursor', p.cursor);
    return params;
  }

  private toSummaryParams(p: CurrentSummaryParams): HttpParams {
    let params = this.toFilterParams(p);
    const set = setter(() => params, next => params = next);
    set('view', p.view);
    set('status', p.status);
    set('cart_owner', p.cartOwner);
    set('with_done', p.withDone ? true : undefined);
    set('with_stale', p.withStale ? true : undefined);
    return params;
  }

  private toFilterParams(f: CurrentFilterParams): HttpParams {
    let params = new HttpParams();
    const set = setter(() => params, next => params = next);
    const setArr = (name: string, values?: Array<string | number>): void => {
      (values ?? []).forEach(v => {
        params = params.append(name, String(v));
      });
    };
    set('period', f.period);
    set('date_from', f.dateFrom);
    set('date_to', f.dateTo);
    setArr('country_ids', f.countryIds);
    setArr('city_ids', f.cityIds);
    setArr('store_ids', f.storeIds);
    set('delivery_type', f.deliveryType);
    set('payment', f.payment);
    set('operator_id', f.operatorId);
    set('important', f.important ? true : undefined);
    setArr('sources', f.sources);
    set('decor', f.decor);
    set('attachments', f.attachments);
    set('client_type', f.clientType);
    setArr('product_info_ids', f.productInfoIds);
    set('courier_id', f.courierId);
    set('with_client', f.withClient === false ? false : undefined);
    return params;
  }
}

/** `HttpParams.set` for a possibly-absent value: nothing is sent for `undefined`/`null`/blank strings. */
function setter(get: () => HttpParams, put: (next: HttpParams) => void): (name: string, value?: string | number | boolean) => void {
  return (name, value) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      put(get().set(name, String(value)));
    }
  };
}
